import { AuthActionResponse, AuthProvider } from '@refinedev/core';
import { setPortalSentryUser } from '@admin-panel/observability';
import { axiosInstance, type PortalAxiosRequestConfig } from './clients/axios';
import { GATEWAY_API_URL, LOCAL_GATEWAY_AUTH_BYPASS_ENABLED } from '@admin-panel/config/env';
import { readRoleOverride } from '@admin-panel/access-control';
import { ensureDeviceFingerprint } from '@admin-panel/utils';
import {
  clearClientStorage,
  handleUnauthorizedSessionExpiry,
  isUnauthorizedHttpError,
  redirectToLogin,
  resetUnauthorizedSessionHandling,
  settleKeptWork,
} from './authSession';

type StoredUserRole = { name?: string };
type StoredUser = {
  roles?: Array<StoredUserRole>;
};

const AUTH_API_URL = GATEWAY_API_URL + '/auth';
const LOCAL_DEVELOPER_ROLES = ['EMS_ADMIN', 'EMS_MANAGER'];
const LOCAL_DEVELOPER_USER = {
  id: -1,
  name: 'Local Developer User',
  roles: LOCAL_DEVELOPER_ROLES.map((role, index) => ({
    id: -(index + 1),
    name: role,
    permissions: [],
    portalCode: 'local',
  })),
};

export enum AuthMode {
  EMAIL_PASSWORD = 'EMAIL_PASSWORD',
  EMAIL_OTP = 'EMAIL_OTP',
  PHONE_OTP = 'PHONE_OTP',
  GOOGLE = 'GOOGLE',
}

export interface AuthRequest {
  authMode: AuthMode;
  id: string;
  secret?: string;
}

export const LKAuthProvider: AuthProvider = {
  login: async (request: AuthRequest): Promise<AuthActionResponse> => {
    if (request) {
      // Storage may have been cleared since bootstrap, including by another tab.
      await ensureDeviceFingerprint();
      const { data: apiResponse } = await axiosInstance['post'](AUTH_API_URL + '/login', request);
      if (apiResponse.status && apiResponse.success === true) {
        const tokeEntry = apiResponse.data;
        resetUnauthorizedSessionHandling();
        // Hub: work kept on the phone by a sign-out mid-work stays only for the same person.
        settleKeptWork(tokeEntry.erpUser?.id);
        localStorage.setItem('user', JSON.stringify({ ...tokeEntry.erpUser }));
        localStorage.setItem('at', `${tokeEntry.token}`);
        localStorage.setItem('rt', `${tokeEntry.refreshToken}`);
        setPortalSentryUser(tokeEntry.erpUser);
        return {
          success: true,
          redirectTo: '/',
        };
      } else {
        return {
          success: false,
          error: { name: apiResponse.status.message, message: 'Login Error' },
        };
      }
    } else {
      return {
        success: false,
        error: { name: 'Failed to fetch Google Token', message: 'Login Error' },
      };
    }
  },
  logout: async () => {
    const token = localStorage.getItem('at');
    if (token && typeof window !== 'undefined') {
      const rt = localStorage.getItem('rt');
      const payload = {
        token: token,
        refreshToken: rt,
      };
      const logoutRequestConfig: PortalAxiosRequestConfig = {
        skipUnauthorizedLogout: true,
      };
      try {
        await axiosInstance['post'](AUTH_API_URL + '/logout', payload, logoutRequestConfig);
      } catch (error) {
        console.warn('Logout request failed. Clearing client session locally.', error);
      }
    }

    clearClientStorage();
    redirectToLogin();

    return {
      success: true,
      redirectTo: '/login',
    };
  },
  onError: async error => {
    if (isUnauthorizedHttpError(error)) {
      handleUnauthorizedSessionExpiry();
      return {};
    }

    return { error };
  },
  check: async () => {
    if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
      return {
        authenticated: true,
      };
    }

    const user = localStorage.getItem('user');
    if (user) {
      return {
        authenticated: true,
      };
    }

    return {
      authenticated: false,
      error: {
        message: 'Check failed',
        name: 'Not authenticated',
      },
      logout: true,
      redirectTo: '/login',
    };
  },
  getPermissions: async () => {
    if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
      return LOCAL_DEVELOPER_ROLES;
    }

    const user = localStorage.getItem('user');
    if (user) {
      const overrideRoles = readRoleOverride();
      if (overrideRoles !== null) {
        return overrideRoles;
      }

      // const userEntity = JSON.parse(user) as IERPUser;
      const userEntity = JSON.parse(user) as StoredUser;
      return userEntity.roles ? userEntity.roles.map(role => role.name).filter(Boolean) : [];
    }
    return [];
  },
  getIdentity: async () => {
    if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
      return LOCAL_DEVELOPER_USER;
    }

    const user = localStorage.getItem('user');
    if (user) {
      const userEntity = JSON.parse(user);
      const overrideRoles = readRoleOverride();
      if (overrideRoles !== null) {
        return {
          ...userEntity,
          roles: overrideRoles.map((role: string, index: number) => ({
            id: -(index + 1),
            name: role,
            permissions: [],
            portalCode: 'local',
          })),
        };
      }

      return userEntity;
    }
    return null;
  },
};
